import React from 'react'
import { observer } from 'mobx-react-lite'
import { getUnixTime } from 'date-fns/getUnixTime'

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { DatePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import { alpha, useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

import { H3, P } from '@mxenabled/mxui'

import {
  useAppConfig,
  useGlobalCopyStore,
  useScreenSize,
  useSettingsStore,
  useUserStore,
  Dialog,
} from '../../../common'
import { type EditableUser } from '../../../core'
import ResetPasswordAction from './actions/ResetPasswordAction'
import VerifyEmailAction from './actions/VerifyEmailAction'
import VerifyPhoneAction from './actions/VerifyPhoneAction'

interface ProfileFormProps {
  availableWidth?: number
  handleOnLeaveClick?: () => void
}

type UserInput = {
  [U in keyof EditableUser]: EditableUser[U]
}

const ProfileForm: React.FC<ProfileFormProps> = ({ availableWidth, handleOnLeaveClick }) => {
  const theme = useTheme()
  const { common: commonCopy, settings: settingsCopy } = useGlobalCopyStore()
  const { isMobile } = useScreenSize(availableWidth)
  const { config } = useAppConfig()
  const { updateUser } = useSettingsStore()
  const { user } = useUserStore()

  const [modifiedUser, setModifiedUser] = React.useState<EditableUser>({
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    phone: user.phone,
    birthday: user.birthday,
    postal_code: user.postal_code,
    credit_score: user.credit_score,
  })
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false)
  const [showLeaveDialog, setShowLeaveDialog] = React.useState(false)
  const boxRef = React.useRef<HTMLDivElement>(null)

  const showVerifyEmail = modifiedUser.email && !user.email_is_verified && !hasUnsavedChanges
  const showVerifyPhone = modifiedUser.phone && !user.phone_is_verified && !hasUnsavedChanges

  React.useEffect(() => {
    if (handleOnLeaveClick) {
      const handleClickOutside = (event: MouseEvent) => {
        if (boxRef.current && !boxRef.current.contains(event.target as Node) && hasUnsavedChanges) {
          setShowLeaveDialog(true)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
    return undefined
  }, [hasUnsavedChanges])

  // eslint-disable-next-line consistent-return
  const formatReadableBirthday = () => {
    if (modifiedUser.birthday) {
      const utc = new Date(modifiedUser.birthday * 1000)
      const year = utc.getUTCFullYear()
      const month = utc.getUTCMonth()
      const day = utc.getUTCDate()
      return new Date(year, month, day)
    }
  }

  const handleSave = async () => {
    await updateUser({ ...user, ...modifiedUser })
    setHasUnsavedChanges(false)
  }

  const handleLeaveDialog = () => {
    setShowLeaveDialog(false)

    if (handleOnLeaveClick) {
      handleOnLeaveClick()
    }
  }

  const handleKeepEditing = () => {
    setShowLeaveDialog(false)
  }

  const handleInput = (update: UserInput) => {
    const changedUser = {
      ...modifiedUser,
      ...update,
    }
    setModifiedUser(changedUser)
    setHasUnsavedChanges(JSON.stringify({ ...user, ...changedUser }) !== JSON.stringify(user))
  }

  return (
    <>
      <Box
        component="form"
        ref={boxRef}
        sx={{
          backgroundColor: 'background.paper',
          p: 24,
          '& .MuiTextField-root': {
            flex: 1,
          },
        }}
      >
        <H3>{settingsCopy.personal_details}</H3>
        <P variant="body1">{settingsCopy.update_information}</P>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={24} sx={{ mb: 24, pt: 24 }}>
          <TextField
            defaultValue={modifiedUser.first_name}
            label={settingsCopy.first_name}
            onChange={(e) => handleInput({ first_name: e.target.value })}
          />
          <TextField
            defaultValue={modifiedUser.last_name}
            label={settingsCopy.last_name}
            onChange={(e) => handleInput({ last_name: e.target.value })}
          />
        </Stack>
        <Stack direction={isMobile ? 'column' : 'row'}>
          {config.display_email_edit_field_in_settings && (
            <Stack sx={{ flex: 1, mb: showVerifyEmail ? 20 : 24, mr: isMobile ? 0 : 24 }}>
              <TextField
                defaultValue={modifiedUser.email}
                label={settingsCopy.email_address}
                onChange={(e) => handleInput({ email: e.target.value })}
              />
              {showVerifyEmail && <VerifyEmailAction />}
            </Stack>
          )}
          {config.display_sms_edit_field_in_settings && (
            <Stack sx={{ flex: 1, mb: showVerifyPhone ? 20 : 24 }}>
              <TextField
                defaultValue={modifiedUser.phone}
                label={settingsCopy.mobile_number}
                onChange={(e) => handleInput({ phone: e.target.value })}
                type="tel"
              />
              {showVerifyPhone && <VerifyPhoneAction />}
            </Stack>
          )}
        </Stack>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={24} sx={{ mb: 24 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={settingsCopy.birthdate}
              onChange={(value) => handleInput({ birthday: getUnixTime(value as Date) })}
              slotProps={{
                nextIconButton: {
                  sx: {
                    color: 'primary.main',
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.1) },
                  },
                },
                previousIconButton: {
                  sx: {
                    color: 'primary.main',
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.1) },
                  },
                },
              }}
              sx={{
                width: '100%',
                '& .MuiIconButton-edgeEnd': {
                  color: 'primary.main',
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.1) },
                },
              }}
              value={formatReadableBirthday()}
            />
          </LocalizationProvider>
          <TextField
            defaultValue={modifiedUser.postal_code}
            label={settingsCopy.zip_code}
            onChange={(e) => handleInput({ postal_code: e.target.value })}
          />
        </Stack>
        <Stack>
          <TextField
            defaultValue={modifiedUser.credit_score}
            label={settingsCopy.credit_score}
            onChange={(e) => handleInput({ credit_score: Number(e.target.value) })}
          />
          {config.display_password_options_in_settings && <ResetPasswordAction />}
          <Button
            disabled={!hasUnsavedChanges}
            onClick={handleSave}
            sx={{ width: isMobile ? '100%' : 124 }}
            variant="contained"
          >
            {commonCopy.save_button}
          </Button>
        </Stack>
      </Box>
      <Dialog
        copy={{ title: settingsCopy.discard_changes }}
        isOpen={showLeaveDialog}
        onClose={handleKeepEditing}
        onSecondaryAction={handleLeaveDialog}
        primaryText={settingsCopy.keep_editing}
        secondaryText={settingsCopy.leave}
      >
        {settingsCopy.discard_changes_message}
      </Dialog>
    </>
  )
}

export default observer(ProfileForm)
